import { FormEvent, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Clock, Eye, EyeOff } from "lucide-react";

import { Alert } from "../../components/Alert/Alert";
import { Button } from "../../components/Button/Button";
import { Input } from "../../components/Input/Input";
import { apiClient } from "../../lib/apiClient";
import { ApiError } from "../../lib/errors";

import { getOAuthClient } from "../mcp/api";
import { AuthNotice } from "./AuthNotice";
import { BrandMark } from "./BrandMark";
import { authorizeClientId, safeNext } from "./safeNext";
import styles from "./AuthLayout.module.css";

interface LoginResponse {
  csrf_token: string;
}

function formatMMSS(totalSec: number): string {
  const sec = Math.max(0, Math.floor(totalSec));
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function LoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // credentialsBad: only set on 401 — drives red borders on both inputs.
  const [credentialsBad, setCredentialsBad] = useState(false);
  const [genericError, setGenericError] = useState<string | null>(null);
  const [retryAfter, setRetryAfter] = useState<number | null>(null);
  const notice = noticeFromParams(params);
  const next = safeNext(params.get("next"));
  // Naming the MCP client makes sign-in read as a step of the connection; a
  // failed lookup shows nothing.
  const clientId = authorizeClientId(next);
  const [connecting, setConnecting] = useState<{
    id: string;
    name: string;
  } | null>(null);
  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    getOAuthClient(clientId)
      .then((c) => {
        if (!cancelled) setConnecting({ id: clientId, name: c.name });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [clientId]);
  const connectingName =
    connecting && connecting.id === clientId ? connecting.name : null;

  // Countdown effect — decrements once per second while a rate-limit window
  // is active. Hides the banner when it reaches zero.
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (retryAfter === null || retryAfter <= 0) {
      if (tickRef.current) {
        clearInterval(tickRef.current);
        tickRef.current = null;
      }
      return;
    }
    tickRef.current = setInterval(() => {
      setRetryAfter((prev) => {
        if (prev === null) return null;
        if (prev <= 1) return null;
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (tickRef.current) {
        clearInterval(tickRef.current);
        tickRef.current = null;
      }
    };
  }, [retryAfter]);

  const rateLimited = retryAfter !== null && retryAfter > 0;
  const formDisabled = submitting || rateLimited;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (formDisabled) return;
    setCredentialsBad(false);
    setGenericError(null);
    setSubmitting(true);
    // Bypass the shared apiClient so we can read Retry-After off the response
    // for the rate-limit countdown (apiClient.ts surfaces the JSON body but
    // not headers; we cannot touch that file from inside features/auth/).
    try {
      const resp = await fetch("/api/admin/auth/login", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, remember }),
      });
      if (resp.status === 429) {
        const ra = parseRetryAfter(resp.headers.get("Retry-After")) ?? 60;
        setRetryAfter(ra);
        return;
      }
      if (resp.status === 401 || resp.status === 403) {
        // Disabled accounts are intentionally indistinguishable from a wrong
        // password (mockup login.html L432: anti-enumeration).
        setCredentialsBad(true);
        return;
      }
      const contentType = resp.headers.get("Content-Type") || "";
      const isJSON = contentType.startsWith("application/json");
      const payload = isJSON ? await resp.json() : null;
      if (!resp.ok) {
        const code = payload?.error?.code ?? `http_${resp.status}`;
        throw new ApiError(code, resp.status, payload?.error?.details);
      }
      const data = payload as LoginResponse;
      if (data?.csrf_token) {
        apiClient.setCSRF(data.csrf_token);
      }
      navigate(next, { replace: true });
    } catch {
      setGenericError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <BrandMark />
        <h1 className={styles.title}>Sign in to your account</h1>
        <div className={styles.subtitle}>
          Welcome back. Enter your details below.
        </div>

        {connectingName && (
          <AuthNotice variant="info" className={styles.noticeSpaced}>
            <b>{connectingName}</b> is asking to connect. Sign in to review the
            request.
          </AuthNotice>
        )}

        {notice && (
          <div style={{ marginBottom: 14 }}>
            <Alert variant="success" title={notice.title}>
              {notice.message}
            </Alert>
          </div>
        )}

        {rateLimited && (
          <div className={styles.rateBanner} role="alert">
            <span className={styles.icon} aria-hidden>
              <Clock size={16} />
            </span>
            <div className={styles.body}>
              Too many sign-in attempts. Please wait{" "}
              <span className={styles.timer}>
                {formatMMSS(retryAfter ?? 0)}
              </span>{" "}
              before trying again.
            </div>
          </div>
        )}

        {credentialsBad && !rateLimited && (
          <div style={{ marginBottom: 14 }}>
            <Alert variant="danger" title="Invalid email or password">
              Check your credentials and try again.
            </Alert>
          </div>
        )}

        {genericError && !credentialsBad && !rateLimited && (
          <div style={{ marginBottom: 14 }}>
            <Alert variant="danger" title="Couldn't sign in">
              {genericError}
            </Alert>
          </div>
        )}

        <form className={styles.form} onSubmit={onSubmit} noValidate>
          <Input
            type="email"
            label="Email address"
            placeholder="you@studio.com"
            autoComplete="username"
            required
            value={email}
            disabled={formDisabled}
            className={credentialsBad ? styles.inputError : undefined}
            aria-invalid={credentialsBad || undefined}
            onChange={(e) => {
              setEmail(e.target.value);
              if (credentialsBad) setCredentialsBad(false);
            }}
          />
          <Input
            type={showPassword ? "text" : "password"}
            label="Password"
            placeholder="••••••••"
            autoComplete="current-password"
            required
            value={password}
            disabled={formDisabled}
            className={credentialsBad ? styles.inputError : undefined}
            aria-invalid={credentialsBad || undefined}
            onChange={(e) => {
              setPassword(e.target.value);
              if (credentialsBad) setCredentialsBad(false);
            }}
            trailingIcon={
              <button
                type="button"
                className={styles.toggleVis}
                aria-label={showPassword ? "Hide password" : "Show password"}
                onClick={() => setShowPassword((v) => !v)}
                disabled={formDisabled}
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            }
          />
          <div className={styles.rowBetween}>
            <label className={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={remember}
                disabled={formDisabled}
                onChange={(e) => setRemember(e.target.checked)}
              />
              Remember me
            </label>
            <Link
              to="/forgot"
              className={styles.link}
              style={
                formDisabled
                  ? { opacity: 0.5, pointerEvents: "none" as const }
                  : undefined
              }
            >
              Forgot password?
            </Link>
          </div>
          <Button
            type="submit"
            size="lg"
            className={styles.submit}
            disabled={formDisabled}
            loading={submitting}
          >
            {submitting ? "Signing in…" : "Sign in"}
          </Button>
        </form>

        <div className={styles.footer}>
          No account? Ask your admin to send you an invite.
        </div>
      </div>
    </div>
  );
}

// parseRetryAfter understands the integer-seconds form of the Retry-After
// header (RFC 9110 §10.2.3). Returns null when the value is missing or not a
// non-negative integer.
function parseRetryAfter(value: string | null): number | null {
  if (!value) return null;
  const n = Number.parseInt(value, 10);
  if (Number.isFinite(n) && n >= 0) return n;
  return null;
}

function noticeFromParams(
  params: URLSearchParams,
): { title: string; message: string } | null {
  if (params.get("reset") === "ok") {
    return {
      title: "Password updated",
      message: "Sign in with your new password to continue.",
    };
  }
  if (params.get("accepted") === "1") {
    return {
      title: "Account created",
      message: "Sign in with your new credentials to continue.",
    };
  }
  return null;
}
